import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { hasClientPrivacy } from "@/lib/client-privacy";

export const STAFF_NOTES_MAX = 10000;

function canView(user) {
  return !!user && ["admin", "staff", "viewer"].includes(user.role) && !hasClientPrivacy(user) &&
    (user.role === "admin" || user.page_permissions == null || user.page_permissions.includes("clients"));
}

function privacyKey(user) {
  const f = Array.isArray(user?.hidden_client_fields) ? user.hidden_client_fields.join(",") : "";
  return `${user?.id || ""}|${user?.role || ""}|${f}|${JSON.stringify(user?.page_permissions)}`;
}

export default function ClientStaffNotes({ clientId, user, isOnline = true }) {
  if (!clientId || !canView(user)) return null;
  return <NotesInner key={`${clientId}|${privacyKey(user)}`} clientId={clientId} role={user.role} isOnline={isOnline} />;
}

function NotesInner({ clientId, role, isOnline }) {
  const canEdit = role === "admin" || role === "staff";
  const [saved, setSaved] = useState(null); // { notes, version, updated_at }
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [success, setSuccess] = useState(false);
  const alive = useRef(true);
  const draftRef = useRef("");
  const savedRef = useRef(null);
  const savingRef = useRef(false);
  const reqId = useRef(0);
  draftRef.current = draft;
  savedRef.current = saved;
  const dirty = saved !== null && draft !== saved.notes;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // force: replace draft with server value. otherwise only if draft is clean.
  const fetchNotes = useCallback(async ({ force = false, quiet = false } = {}) => {
    const my = ++reqId.current;
    if (!quiet) { setLoading(true); setLoadError(""); }
    try {
      const { data } = await api.get(`/clients/${clientId}/staff-notes`);
      if (!alive.current || my !== reqId.current) return;
      const env = { notes: data?.notes || "", version: data?.version ?? 0, updated_at: data?.updated_at || null };
      if (!force && (dirtyRef.current || savingRef.current)) return;
      setSaved(env); setDraft(env.notes);
      setConflict(false); setSaveError(""); setSuccess(false);
    } catch (err) {
      if (!alive.current || my !== reqId.current) return;
      if (!quiet) setLoadError(formatApiError(err));
    } finally {
      if (alive.current && my === reqId.current && !quiet) setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  useEffect(() => {
    function onFocus() {
      if (dirtyRef.current || savingRef.current || !savedRef.current) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      fetchNotes({ quiet: true });
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchNotes]);

  async function save() {
    if (!canEdit || !saved || loading || savingRef.current || !dirty || draft.length > STAFF_NOTES_MAX) return;
    if (!isOnline) { setSaveError("You're offline. Staff notes can only be saved while online."); return; }
    const sent = draft;
    // A focus refresh started before editing must not replace a successful save.
    ++reqId.current;
    savingRef.current = true;
    setSaving(true); setSaveError(""); setSuccess(false); setConflict(false);
    try {
      const { data } = await api.put(`/clients/${clientId}/staff-notes`, { notes: sent, version: saved.version });
      if (!alive.current) return;
      const env = { notes: data?.notes ?? sent, version: data?.version ?? saved.version + 1, updated_at: data?.updated_at || null };
      setSaved(env);
      if (draftRef.current === sent) setDraft(env.notes);
      setSuccess(true);
    } catch (err) {
      if (!alive.current) return;
      if (err?.response?.status === 409) setConflict(true);
      else setSaveError(formatApiError(err));
    } finally {
      savingRef.current = false;
      if (alive.current) setSaving(false);
    }
  }

  function reloadLatest() {
    if (dirty && !window.confirm("Reload the latest notes? Your unsaved changes will be discarded.")) return;
    fetchNotes({ force: true });
  }

  function cancel() { if (saved) { setDraft(saved.notes); setSaveError(""); setConflict(false); setSuccess(false); } }

  const tooLong = draft.length > STAFF_NOTES_MAX;
  const border = { borderColor: "var(--fb-border)" };
  return (
    <section className="fb-card p-6 space-y-3 print:hidden" data-testid="client-staff-notes" aria-labelledby="staff-notes-heading">
      <div>
        <h3 id="staff-notes-heading" className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Staff notes</h3>
        <p id="staff-notes-help" className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
          Internal notes for staff. Not visible to clients.
        </p>
      </div>
      {!isOnline && (
        <p role="status" data-testid="staff-notes-offline" className="text-sm font-medium" style={{ color: "var(--fb-warning)" }}>
          You're offline. Staff notes are only available online{saved ? "; your draft is kept until you reconnect." : "."}
        </p>
      )}
      {loading && !saved && <div data-testid="staff-notes-loading" className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading staff notes…</div>}
      {loadError && !saved && (
        <div role="alert" data-testid="staff-notes-load-error" className="text-sm flex items-center gap-3" style={{ color: "var(--fb-error)" }}>
          <span>{loadError}</span>
          <button type="button" onClick={() => fetchNotes()} className="px-3 py-1.5 rounded-lg border font-medium" style={border}>Retry</button>
        </div>
      )}
      {saved && (
        <>
          {canEdit ? (
            <div>
              <label htmlFor="staff-notes-input" className="block text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#8B918B" }}>Staff notes</label>
              <textarea
                id="staff-notes-input"
                data-testid="staff-notes-input"
                rows={5}
                value={draft}
                disabled={saving || loading}
                onChange={(e) => { setDraft(e.target.value); setSuccess(false); }}
                aria-describedby="staff-notes-help staff-notes-count"
                aria-invalid={tooLong || undefined}
                className="w-full px-3 py-2 rounded-lg border outline-none focus:border-[#052F66] text-sm"
                style={{ ...border, background: "#FAFAF8" }}
              />
              <div id="staff-notes-count" data-testid="staff-notes-count" className="text-xs mt-1 tabular-nums" style={{ color: tooLong ? "var(--fb-error)" : "var(--fb-text-muted)" }}>
                {draft.length.toLocaleString()} / {STAFF_NOTES_MAX.toLocaleString()} characters
                {tooLong && ` - shorten by ${(draft.length - STAFF_NOTES_MAX).toLocaleString()} to save`}
              </div>
            </div>
          ) : (
            <div>
              <div className="block text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#8B918B" }}>Staff notes</div>
              <div data-testid="staff-notes-readonly" role="region" aria-label="Staff notes (read only)" className="text-sm whitespace-pre-wrap break-words">
                {saved.notes || <span style={{ color: "var(--fb-text-muted)" }}>No staff notes.</span>}
              </div>
            </div>
          )}
          {conflict && (
            <div role="alert" data-testid="staff-notes-conflict" className="p-3 rounded-xl fb-status-warning text-sm space-y-2">
              <div>Another staff member changed these notes since you opened them. Your draft is kept below and has not been saved.</div>
              <button type="button" data-testid="staff-notes-reload" onClick={reloadLatest} className="px-3 py-1.5 rounded-lg border font-medium bg-white" style={border}>Reload latest notes</button>
            </div>
          )}
          {saveError && <div role="alert" data-testid="staff-notes-error" className="text-sm" style={{ color: "var(--fb-error)" }}>{saveError} Your draft is kept.</div>}
          {success && !dirty && <div role="status" data-testid="staff-notes-success" className="text-sm font-medium" style={{ color: "var(--fb-success)" }}>Staff notes saved.</div>}
          {loadError && <div role="alert" className="text-sm" style={{ color: "var(--fb-error)" }}>{loadError}</div>}
          {canEdit && (
            <div className="flex items-center gap-3">
              <button type="button" data-testid="staff-notes-save" onClick={save}
                disabled={!dirty || saving || loading || tooLong}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: "var(--fb-primary)" }}>
                {saving ? "Saving…" : saveError ? "Retry save" : "Save notes"}
              </button>
              <button type="button" data-testid="staff-notes-cancel" onClick={cancel}
                disabled={!dirty || saving || loading}
                className="px-4 py-2 rounded-xl text-sm font-medium border disabled:opacity-50" style={border}>
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
